import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Input, Textarea, Slider, Select, SelectItem, Chip, Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useWorkflowStore, type Trigger } from "@/stores/WorkflowStore";
import { usePlaygroundStore } from "@/stores/PlaygroundStore";
import { useAvailableModels } from "@/hooks/playground/useAvailableModels";
import { logger } from "@/telemetry";

interface AgentPropertiesPanelProps {
  agentId: string;
}

const AGENT_TYPES = [
  { key: "chat_agent", label: "Chat Agent" },
  { key: "crew_agent", label: "Crew Agent" },
];

export const AgentPropertiesPanel: React.FC<AgentPropertiesPanelProps> = ({ agentId }) => {
  const { t } = useTranslation();
  const { workflow, setWorkflow } = useWorkflowStore();
  const { platformClient } = useWebuildAPIClient();
  const availableModels = useAvailableModels();
  const [availableTools, setAvailableTools] = useState<
    Array<{ id: string; name: string; description: string }>
  >([]);

  const { setSelectedNode } = usePlaygroundStore();
  const [editableName, setEditableName] = useState(agentId);

  // Sync local name when agentId prop changes (e.g. after rename)
  useEffect(() => {
    setEditableName(agentId);
  }, [agentId]);

  const agent = workflow?.agents?.[agentId];

  // Find parent team (if this agent is a team member)
  const parentTeamId = useMemo(() => {
    if (!workflow?.teams) return null;

    return (
      Object.entries(workflow.teams).find(([, team]) => team.members.includes(agentId))?.[0] ?? null
    );
  }, [workflow, agentId]);

  const renameAgent = useCallback(
    (newName: string) => {
      const trimmed = newName.trim().replace(/\s+/g, "_");

      if (!trimmed || trimmed === agentId || !workflow) return;

      // Ensure unique
      if (workflow.agents[trimmed]) {
        setEditableName(agentId);

        return;
      }

      const { [agentId]: agentData, ...restAgents } = workflow.agents;
      const renamedAgents = { ...restAgents, [trimmed]: agentData };

      // Update trigger sources referencing the old name
      Object.entries(renamedAgents).forEach(([id, a]) => {
        if (a.triggers) {
          renamedAgents[id] = {
            ...a,
            triggers: a.triggers.map((t: Trigger) =>
              t.source === agentId ? { ...t, source: trimmed } : t,
            ),
          };
        }
      });

      // Update team members and team triggers
      let updatedTeams = workflow.teams;

      if (updatedTeams) {
        const teams = { ...updatedTeams };

        Object.entries(teams).forEach(([teamId, team]) => {
          const membersUpdated = team.members.includes(agentId);
          const triggersUpdated = team.triggers?.some((t: Trigger) => t.source === agentId);

          if (membersUpdated || triggersUpdated) {
            teams[teamId] = {
              ...team,
              members: membersUpdated
                ? team.members.map((m) => (m === agentId ? trimmed : m))
                : team.members,
              triggers: triggersUpdated
                ? (team.triggers || []).map((t: Trigger) =>
                    t.source === agentId ? { ...t, source: trimmed } : t,
                  )
                : team.triggers,
            };
          }
        });
        updatedTeams = teams;
      }

      setWorkflow({ ...workflow, agents: renamedAgents, teams: updatedTeams });
      setSelectedNode(trimmed);
    },
    [agentId, workflow, setWorkflow, setSelectedNode],
  );

  useEffect(() => {
    const fetchTools = async () => {
      if (!platformClient) return;
      try {
        const tools = await platformClient.tools.list();

        setAvailableTools(
          tools.map((t: any) => ({
            id: t.id,
            name: t.name,
            description: t.description || "",
          })),
        );
      } catch (error) {
        logger.error("Failed to fetch tools", error);
      }
    };

    fetchTools();
  }, [platformClient]);

  if (!agent || !workflow) {
    return (
      <div className="p-4 text-center text-default-500">
        <Icon className="w-8 h-8 mx-auto mb-2" icon="solar:user-hand-up-outline" />
        <p className="text-sm">{t("Agent not found")}</p>
      </div>
    );
  }

  const updateParameter = (key: string, value: any) => {
    const updatedWorkflow = {
      ...workflow,
      agents: {
        ...workflow.agents,
        [agentId]: {
          ...workflow.agents[agentId],
          parameters: {
            ...workflow.agents[agentId].parameters,
            [key]: value,
          },
        },
      },
    };

    setWorkflow(updatedWorkflow);
  };

  const updateAgentField = (key: string, value: any) => {
    const updatedWorkflow = {
      ...workflow,
      agents: {
        ...workflow.agents,
        [agentId]: {
          ...workflow.agents[agentId],
          [key]: value,
        },
      },
    };

    setWorkflow(updatedWorkflow);
  };

  const toggleTool = (toolId: string) => {
    const currentTools = (agent.parameters?.tools as string[]) || [];
    const updatedTools = currentTools.includes(toolId)
      ? currentTools.filter((t) => t !== toolId)
      : [...currentTools, toolId];

    updateParameter("tools", updatedTools);
  };

  const currentModel = (agent.parameters?.model as string) || "";
  const currentTemperature = parseFloat((agent.parameters?.temperature as string) || "0.7");
  const currentMaxTokens = (agent.parameters?.max_tokens as string) || "2000";
  const currentSystemMessage = (agent.parameters?.system_message as string) || "";
  const currentTools = (agent.parameters?.tools as string[]) || [];

  return (
    <div className="space-y-4">
      {/* Back to team breadcrumb */}
      {parentTeamId && (
        <Button
          className="mb-2 text-xs"
          size="sm"
          startContent={<Icon className="w-3.5 h-3.5" icon="solar:arrow-left-linear" />}
          variant="light"
          onPress={() => setSelectedNode(parentTeamId)}
        >
          {t("Back to team")} &ldquo;{parentTeamId}&rdquo;
        </Button>
      )}

      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-blue-500" icon="solar:user-hand-up-outline" />
        <h3 className="text-sm font-semibold">{t("Agent Configuration")}</h3>
      </div>

      {/* Agent Name (ID) */}
      <Input
        label={t("Agent Name")}
        placeholder={t("e.g. researcher, summariser")}
        size="sm"
        value={editableName}
        onBlur={() => renameAgent(editableName)}
        onKeyDown={(e) => {
          if (e.key === "Enter") renameAgent(editableName);
          if (e.key === "Escape") setEditableName(agentId);
        }}
        onValueChange={setEditableName}
      />

      {/* Agent Type */}
      <Select
        label={t("Agent Type")}
        placeholder={t("Select agent type")}
        selectedKeys={agent.type ? [agent.type] : []}
        size="sm"
        onSelectionChange={(keys) => {
          const selectedType = Array.from(keys)[0] as string;

          if (selectedType) updateAgentField("type", selectedType);
        }}
      >
        {AGENT_TYPES.map((type) => (
          <SelectItem key={type.key}>{type.label}</SelectItem>
        ))}
      </Select>

      {/* Model */}
      <Select
        label={t("Model")}
        placeholder={t("Select model")}
        selectedKeys={currentModel ? [currentModel] : []}
        size="sm"
        onSelectionChange={(keys) => {
          const selectedModel = Array.from(keys)[0] as string;

          if (selectedModel) updateParameter("model", selectedModel);
        }}
      >
        {availableModels.map((model) => (
          <SelectItem key={model.id}>{model.name}</SelectItem>
        ))}
      </Select>

      {/* Temperature */}
      <div>
        <label className="text-xs text-default-600 mb-1 block">
          {t("Temperature")}: {currentTemperature}
        </label>
        <Slider
          aria-label={t("Temperature")}
          maxValue={1}
          minValue={0}
          size="sm"
          step={0.1}
          value={currentTemperature}
          onChange={(value) => {
            const numValue = Array.isArray(value) ? value[0] : value;

            updateParameter("temperature", String(numValue));
          }}
        />
      </div>

      {/* Max Tokens */}
      <Input
        label={t("Max Tokens")}
        size="sm"
        type="number"
        value={currentMaxTokens}
        onValueChange={(value) => updateParameter("max_tokens", value)}
      />

      {/* System Message */}
      <Textarea
        label={t("System Message")}
        maxRows={8}
        minRows={3}
        placeholder={t("Enter system instructions...")}
        size="sm"
        value={currentSystemMessage}
        onValueChange={(value) => updateParameter("system_message", value)}
      />

      {/* Description */}
      <Textarea
        label={t("Description")}
        maxRows={3}
        minRows={1}
        placeholder={t("Agent role description (used by team selector)...")}
        size="sm"
        value={agent.description || ""}
        onValueChange={(value) => updateAgentField("description", value)}
      />

      {/* Tools */}
      <div>
        <label className="text-xs text-default-600 mb-2 block">{t("Tools")}</label>
        <div className="flex flex-wrap gap-1">
          {availableTools.map((tool) => (
            <Chip
              key={tool.id}
              className="cursor-pointer"
              color={currentTools.includes(tool.id) ? "primary" : "default"}
              size="sm"
              variant={currentTools.includes(tool.id) ? "solid" : "flat"}
              onClick={() => toggleTool(tool.id)}
            >
              {tool.name}
            </Chip>
          ))}
          {availableTools.length === 0 && (
            <span className="text-xs text-default-400">{t("Loading tools...")}</span>
          )}
        </div>
      </div>
    </div>
  );
};
